<template>
  <RouterLink to="/">
    <div class="auth-logo d-flex align-center gap-x-3">
      <img
        :src="appLogo"
        alt="app logo"
        style="block-size: 30px;"
      >
    </div>
  </RouterLink>

  <VRow
    no-gutters
    class="auth-wrapper bg-surface"
  >
    <VCol
      md="8"
      class="d-none d-md-flex"
    >
      <div class="position-relative  w-100 me-0">
        <img
          class="auth-background"
          :src="loginBackgroundImg"
          alt="background"
        >
      </div>
    </VCol>

    <VCol
      cols="12"
      md="4"
      class="auth-card-v2 d-flex align-center justify-center"
      style="background-color: white;"
    >
      <VCard
        flat
        
        class="mt-12 mt-sm-0 pa-4 w-100"
        style="background-color: white;"
      >
        <div class="text-center mb-10">
          <RouterLink to="/">
            <img
              src="@/assets/jkt/logo1.png"
              alt="logo"
              class="logo"
            >
          </RouterLink>
          <h4 class="text-h4 mb-1 text-black">
            JKT GATEWAY LOGIN
          </h4>

          <h4 class="text-h6 mb-1 text-black">
            Please login to continue
          </h4>
        </div>

        <!-- Notification Alert -->
        <VAlert
          v-if="errorMessage"
          type="error"
          dismissible
          class="mb-4"
          @click:close="errorMessage = ''"
        >
          {{ errorMessage }}
        </VAlert>

        <VCardText>
          <VForm @submit.prevent="handleLogin">
            <VRow>
              <!-- email -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.email"
                  name="username"
                  autocomplete="username"
                  autofocus
                  type="email"
                  placeholder="Username"
                  class="text-black input-black"
                />
              </VCol>

              <!-- password -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.password"
                  name="password"
                  autocomplete="current-password"
                  placeholder="Password"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"   
                  class="text-black input-black"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />
              </VCol>

              <!-- remember me -->
              <div class="d-flex align-center flex-wrap justify-space-between mt-2 mb-4 text-black">
                <VCheckbox
                  v-model="form.remember"
                  label="Remember me"
                  class="text-black"
                />
              </div>

              <!-- sign in button -->
              <VCol cols="12">
                <VBtn
                  block
                  type="submit"
                >
                  Login
                </VBtn>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<script setup>
import loginBackgroundImg from '@/assets/jkt/loginBack.png'
import appLogo from '@/assets/jkt/logoJKT.png'
import { useHttpService } from "@/httpService"
import { useAuthStore } from "@/store/auth"
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios';

const router = useRouter()
const authStore = useAuthStore() 

const { loading, error, httpDelete, httpPostPut, httpGet, httpPost } = useHttpService()

//mapAction always in the methods while mapState always in computed

definePage({ meta: { layout: 'blank' } })

const form = ref({
  email: '',
  password: '',
  remember: false,
})

const isPasswordVisible = ref(false)
const errorMessage = ref('')

// Retrieve credentials from local storage on component mount
onMounted(() => {
  const savedEmail = localStorage.getItem('email')
  const savedPassword = localStorage.getItem('password')
  const remember = localStorage.getItem('remember') === 'true'

  if (savedEmail && savedPassword && remember) {
    form.value.email = savedEmail
    form.value.password = savedPassword
    form.value.remember = remember
  }
})

const loginApiUrl = import.meta.env.VITE_LOGIN_API_URL;

const handleLogin = async () => {
  try {
    errorMessage.value = '' // Clear any existing error message

    const response = await axios.post(`${loginApiUrl}login`, form.value);
    console.log('Login successful:', response)
    const token = response.data.token ? response.data.token : response.token;

    if (!token) {
      throw new Error('Token not found in login response.');
    }

    // Store token in local storage
    localStorage.setItem('token', token)

    // Get user info
    const userResponse = await axios.get(`${loginApiUrl}user`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Store user in local storage
    localStorage.setItem('user', JSON.stringify(userResponse.data))

    // Set roles (hardcoded as manager for now)
    const roles = userResponse.data.roles;

    localStorage.setItem('roles', JSON.stringify(roles));

    // Save credentials if "Remember me" is checked
    if (form.value.remember) {
      localStorage.setItem('email', form.value.email)
      localStorage.setItem('password', form.value.password)
      localStorage.setItem('remember', 'true')
    } else {
      localStorage.removeItem('email')
      localStorage.removeItem('password')
      localStorage.removeItem('remember')
    }

    // router.push('/')

    // With this:
    router.push('/').then(() => {
      window.location.reload()
    })
  } catch (err) {
    console.error('Login failed:', err)
    errorMessage.value = 'These credentials do not match our records.'
  }
}
</script>

<style lang="scss">
@use "@core/scss/template/pages/page-auth.scss";

.text-black {
  color: black !important;
}

.v-label,
.v-input__control,
.v-input__control .v-input__slot,
.v-input__slot input,
.v-checkbox__control,
.v-checkbox__label {
  color: black !important;
}

.input-black input::placeholder {
  color: black !important;
  opacity: 1; /* Override default opacity for better visibility */
}

.input-black input {
  color: black !important;
}

.tabler-eye,
.tabler-eye-off {
  color: black !important;
}

.logo {
  display: block;
  block-size: 80px;
  margin-block: 0;
  margin-inline: auto;
}

.auth-background {
  block-size: 100%;
  inline-size: 100%;
  object-fit: cover;
}

.position-relative {
  overflow: hidden; /* Prevent overflow from the image */
  block-size: 100vh; /* Set a height for the container */
}
</style>
